import React, { useCallback, useEffect, useState } from 'react';
import api from '../api';
import { useAuth } from '../context/AuthContext';

// 같은 화면을 stdtype 만 바꿔 두 메뉴에서 쓴다: 'TOS' = 훈련운영기준, 'ESR' = 교육규정
const TITLES = { TOS: '훈련운영기준', ESR: '교육규정' };

const today = () => {
  const d = new Date();
  const pad = (n) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
};

const emptyForm = () => ({ stdreg: '', updateat: today(), reason: '', pdfFile: null, hwpxFile: null });

// 목록의 파일 아이콘: 문서 모양에 PDF(빨강) / 한글(파랑) 띠를 둔 작은 아이콘
export function FileIcon({ kind }) {
  const isPdf = kind === 'pdf';
  const color = isPdf ? '#dc2626' : '#2563eb';
  return (
    <svg viewBox="0 0 24 28" width="22" height="26" aria-hidden="true">
      <path d="M3 1.5h12l6 6V25a1.5 1.5 0 0 1-1.5 1.5h-16A1.5 1.5 0 0 1 2 25V3A1.5 1.5 0 0 1 3.5 1.5z" fill="#ffffff" stroke={color} strokeWidth="1.6" strokeLinejoin="round" />
      <path d="M15 1.5v5a1.5 1.5 0 0 0 1.5 1.5H21" fill="none" stroke={color} strokeWidth="1.6" strokeLinejoin="round" />
      <rect x="2.8" y="14" width="18.4" height="8" rx="1.2" fill={color} />
      <text x="12" y="20.3" textAnchor="middle" fontSize={isPdf ? '6.6' : '6.2'} fontWeight="700" fill="#ffffff" fontFamily="Arial, sans-serif">
        {isPdf ? 'PDF' : 'HWPX'}
      </text>
    </svg>
  );
}

export default function StandardRegulations({ stdtype }) {
  const { user } = useAuth();
  const isAdmin = user?.role === 'admin' || user?.role === 'staff';
  const title = TITLES[stdtype] || '규정';

  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState('');
  const [editing, setEditing] = useState(null); // null=닫힘, 'new'=등록, sregid=수정
  const [form, setForm] = useState(emptyForm());
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await api.get('/standards', { params: { stdtype } });
      setRows(res.data);
    } finally {
      setLoading(false);
    }
  }, [stdtype]);

  // 메뉴를 바꿔 훈련운영기준 <-> 교육규정으로 오면 같은 화면이 다시 쓰이므로 상태를 비우고 다시 불러온다
  useEffect(() => {
    setEditing(null);
    setMessage('');
    setRows([]);
    load();
  }, [load]);

  const editingRow = typeof editing === 'number' ? rows.find((r) => r.sregid === editing) : null;

  const openNew = () => {
    setForm(emptyForm());
    setEditing('new');
    setMessage('');
  };

  const openEdit = (r) => {
    setForm({ stdreg: r.stdreg, updateat: r.updateat || today(), reason: r.reason || '', pdfFile: null, hwpxFile: null });
    setEditing(r.sregid);
    setMessage('');
  };

  const closeForm = () => setEditing(null);

  const handleSave = async (e) => {
    e.preventDefault();
    if (!form.stdreg.trim()) {
      setMessage('규정명을 입력하세요.');
      return;
    }
    if (!form.updateat) {
      setMessage('개정일자를 입력하세요.');
      return;
    }
    if (editing === 'new' && !form.pdfFile && !form.hwpxFile) {
      setMessage('PDF 파일과 HWPX 파일을 선택하세요.');
      return;
    }
    const data = new FormData();
    data.append('stdreg', form.stdreg.trim());
    data.append('updateat', form.updateat);
    data.append('reason', form.reason);
    if (form.pdfFile) data.append('pdf_file', form.pdfFile);
    if (form.hwpxFile) data.append('hwpx_file', form.hwpxFile);
    setSaving(true);
    setMessage('');
    try {
      if (editing === 'new') {
        data.append('stdtype', stdtype);
        await api.post('/standards', data);
      } else {
        await api.put(`/standards/${editing}`, data);
      }
      setEditing(null);
      await load();
      setMessage('저장되었습니다.');
    } catch (err) {
      setMessage(err.response?.data?.detail || '저장하지 못했습니다.');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (r) => {
    if (!window.confirm(`"${r.stdreg}" (${r.updateat}) 규정을 삭제하시겠습니까?\nPDF, HWPX 파일도 함께 삭제됩니다.`)) return;
    try {
      await api.delete(`/standards/${r.sregid}`);
      await load();
      setMessage('삭제되었습니다.');
    } catch (err) {
      setMessage(err.response?.data?.detail || '삭제하지 못했습니다.');
    }
  };

  // 파일을 여는 이 요청이 서버에서 열람건수를 올린다. PDF 는 새 탭에서 바로 보고, HWPX 는 내려받는다.
  const openFile = async (r, kind) => {
    const isPdf = kind === 'pdf';
    // 비동기 요청이 끝난 뒤 열면 팝업 차단될 수 있어, PDF 는 클릭 시점에 빈 탭을 먼저 연다
    const win = isPdf ? window.open('', '_blank') : null;
    try {
      const res = await api.get(`/standards/${r.sregid}/file`, { params: { kind }, responseType: 'blob' });
      const url = window.URL.createObjectURL(res.data);
      if (win) {
        win.location.href = url;
      } else {
        const a = document.createElement('a');
        a.href = url;
        a.download = (isPdf ? r.pdfname : r.hwpname) || `file.${kind}`;
        document.body.appendChild(a);
        a.click();
        a.remove();
      }
      setTimeout(() => window.URL.revokeObjectURL(url), 60000);
      await load();
    } catch (err) {
      if (win) win.close();
      setMessage('파일을 열 수 없습니다. 서버에 파일이 없거나 삭제되었을 수 있습니다.');
    }
  };

  return (
    <div className="page std-page">
      <div className="page-header">
        <h2>{title}</h2>
        {isAdmin && editing === null && (
          <button type="button" className="btn-primary std-add-btn" onClick={openNew}>
            {title} 등록
          </button>
        )}
      </div>

      {editing !== null && (
        <form className="card std-form" onSubmit={handleSave}>
          <h3>{editing === 'new' ? `${title} 등록` : `${title} 수정`}</h3>
          <div className="std-form-grid">
            <div className="form-field">
              <label>규정명</label>
              <input
                type="text"
                maxLength={30}
                value={form.stdreg}
                onChange={(e) => setForm({ ...form, stdreg: e.target.value })}
                placeholder={`예) ${title} 개정 2`}
              />
            </div>
            <div className="form-field">
              <label>개정일자</label>
              <input type="date" value={form.updateat} onChange={(e) => setForm({ ...form, updateat: e.target.value })} />
            </div>
          </div>
          <div className="form-field">
            <label>개정사유</label>
            <textarea
              rows={3}
              value={form.reason}
              onChange={(e) => setForm({ ...form, reason: e.target.value })}
              placeholder="개정 사유와 주요 변경 내용을 적습니다."
            />
          </div>
          <div className="std-form-grid std-file-grid">
            <div className="form-field">
              <label>PDF 파일 (.pdf)</label>
              <input
                type="file"
                accept=".pdf,application/pdf"
                onChange={(e) => setForm({ ...form, pdfFile: e.target.files?.[0] || null })}
              />
              {editingRow?.pdfname && <span className="std-hint">현재: {editingRow.pdfname} (바꿀 때만 선택)</span>}
            </div>
            <div className="form-field">
              <label>HWPX 파일 (.hwpx)</label>
              <input
                type="file"
                accept=".hwpx"
                onChange={(e) => setForm({ ...form, hwpxFile: e.target.files?.[0] || null })}
              />
              {editingRow?.hwpname && <span className="std-hint">현재: {editingRow.hwpname} (바꿀 때만 선택)</span>}
            </div>
          </div>
          <span className="std-hint std-hint-block">
            파일당 30MB 이하입니다.{editing !== 'new' && ' 파일을 바꾸면 열람건수가 0으로 초기화됩니다.'}
          </span>
          <div className="std-form-actions">
            <button type="submit" className="btn-primary std-save-btn" disabled={saving}>
              {saving ? '저장 중...' : '저장'}
            </button>
            <button type="button" className="btn-ghost" onClick={closeForm}>취소</button>
          </div>
        </form>
      )}

      {message && <div className="form-message std-message">{message}</div>}

      <div className="card">
        <div className="table-scroll">
          <table className="data-table std-table">
            <thead>
              <tr>
                <th className="std-col-no">번호</th>
                <th className="std-col-name">규정명</th>
                <th className="std-col-date">개정일자</th>
                <th className="std-col-file">첨부파일</th>
                <th className="std-col-read">열람</th>
                {isAdmin && <th className="std-col-admin">관리</th>}
              </tr>
            </thead>
            <tbody>
              {rows.map((r, i) => (
                <React.Fragment key={r.sregid}>
                <tr className={`std-main-row${r.reason ? ' has-reason' : ''}`}>
                  <td>{rows.length - i}</td>
                  <td className="std-name">
                    {r.stdreg}
                    {i === 0 && <span className="std-current-badge">현행</span>}
                  </td>
                  <td>{r.updateat}</td>
                  <td className="std-file">
                    <div className="std-file-cell">
                      <span className="std-file-name">{r.pdfname || r.hwpname || '파일 없음'}</span>
                      <span className="std-file-icons">
                        <button
                          type="button"
                          className="std-icon-btn"
                          disabled={!r.has_pdf}
                          onClick={() => openFile(r, 'pdf')}
                          title={r.has_pdf ? 'PDF 파일 열기' : 'PDF 파일 없음'}
                          aria-label="PDF 파일 열기"
                        >
                          <FileIcon kind="pdf" />
                        </button>
                        <button
                          type="button"
                          className="std-icon-btn"
                          disabled={!r.has_hwpx}
                          onClick={() => openFile(r, 'hwpx')}
                          title={r.has_hwpx ? 'HWPX 파일 열기 (내려받기)' : 'HWPX 파일 없음'}
                          aria-label="HWPX 파일 열기"
                        >
                          <FileIcon kind="hwpx" />
                        </button>
                      </span>
                    </div>
                  </td>
                  <td>{r.readcnt}</td>
                  {isAdmin && (
                    <td className="std-admin">
                      <button type="button" className="btn-ghost std-small-btn" onClick={() => openEdit(r)}>수정</button>
                      <button type="button" className="btn-ghost std-small-btn std-del-btn" onClick={() => handleDelete(r)}>삭제</button>
                    </td>
                  )}
                </tr>
                {r.reason && (
                  // 개정사유는 글자가 잘 보이도록 규정 행 바로 아래 줄에 넓게 보여준다
                  <tr className="std-reason-row">
                    <td colSpan={2} className="std-reason-gap" />
                    <td colSpan={isAdmin ? 4 : 3} className="std-reason">
                      <span className="std-reason-label">개정사유</span>
                      <span className="std-reason-text">{r.reason}</span>
                    </td>
                  </tr>
                )}
                </React.Fragment>
              ))}
              {!loading && rows.length === 0 && (
                <tr>
                  <td colSpan={isAdmin ? 6 : 5} className="empty-row">
                    등록된 {title}이(가) 없습니다.{isAdmin ? ` 위의 [${title} 등록] 버튼으로 추가하세요.` : ''}
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
